import { getRouteApi } from '@tanstack/react-router'
import { GraduationCap, Search, SlidersHorizontal, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { PageIntro } from '@/components/page-intro'
import { PromoBanner } from '@/components/promo'
import { CourseCard, CourseCardSkeleton } from '@/components/course-card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Select } from '@/components/ui/field'
import { useCategories, useCourses } from '@/features/catalog/api'
import { formatNumber } from '@/lib/format'
import { cn } from '@/lib/cn'

const route = getRouteApi('/public/kelas')

const sortOptions = [
  { value: 'newest', label: 'Terbaru' },
  { value: 'popular', label: 'Paling banyak peserta' },
  { value: 'rating', label: 'Rating tertinggi' },
  { value: 'price_asc', label: 'Harga termurah' },
  { value: 'price_desc', label: 'Harga tertinggi' },
]

const levelOptions = [
  { value: '', label: 'Semua tingkat' },
  { value: 'beginner', label: 'Pemula' },
  { value: 'intermediate', label: 'Menengah' },
  { value: 'advanced', label: 'Lanjutan' },
]

const formatOptions = [
  { value: '', label: 'Semua format' },
  { value: 'self_paced', label: 'Belajar mandiri' },
  { value: 'live', label: 'Kelas live' },
  { value: 'hybrid', label: 'Mandiri + live' },
]

export function CoursesPage() {
  const search = route.useSearch()
  const navigate = route.useNavigate()

  // Kolom pencarian dikelola sebagai state lokal lalu disalin ke URL setelah
  // pengetikan berhenti sejenak. Menulis ke URL pada setiap ketukan membuat
  // riwayat peramban penuh dan permintaan API bertubi-tubi.
  const [queryDraft, setQueryDraft] = useState(search.q ?? '')

  useEffect(() => {
    setQueryDraft(search.q ?? '')
  }, [search.q])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if ((search.q ?? '') === queryDraft) return
      navigate({ search: (previous) => ({ ...previous, q: queryDraft || undefined, page: 1 }), replace: true })
    }, 350)

    return () => window.clearTimeout(timer)
  }, [queryDraft, navigate, search.q])

  const { data, isLoading, isFetching } = useCourses({
    q: search.q,
    category: search.category,
    level: search.level,
    format: search.format,
    sort: search.sort,
    page: search.page,
  })
  const { data: categoriesData } = useCategories()

  const courses = data?.courses ?? []
  const meta = data?.meta
  const activeFilters = [search.category, search.level, search.format].filter(Boolean).length

  const setSearch = (patch: Record<string, string | number | undefined>) => {
    navigate({ search: (previous) => ({ ...previous, ...patch, page: 1 }) })
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6">
      <PageIntro
        accent="kelas"
        eyebrow="Kelas"
        icon={<GraduationCap className="size-3.5" />}
        title="Katalog kelas"
        description="Kelas yang dibawakan praktisi, dengan kurikulum yang terbuka sebelum Anda membayar."
      />

      <PromoBanner slot="katalog_banner" className="mt-8" />

      <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle" />
          <Input
            value={queryDraft}
            onChange={(event) => setQueryDraft(event.target.value)}
            placeholder="Cari kelas, topik, atau nama pengajar"
            aria-label="Cari kelas"
            className="pl-10"
          />
        </div>

        {/* Pada layar sempit penyaring memenuhi lebar agar area sentuhnya
            besar; pada layar lebar ketiganya berjajar dengan lebar tetap. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:flex lg:flex-wrap">
          <Select
            value={search.level ?? ''}
            onChange={(event) => setSearch({ level: event.target.value || undefined })}
            aria-label="Tingkat kelas"
            className="lg:w-44"
          >
            {levelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <Select
            value={search.format ?? ''}
            onChange={(event) => setSearch({ format: event.target.value || undefined })}
            aria-label="Format kelas"
            className="lg:w-44"
          >
            {formatOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <Select
            value={search.sort ?? 'newest'}
            onChange={(event) => setSearch({ sort: event.target.value })}
            aria-label="Urutkan"
            className="lg:w-52"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {categoriesData?.categories.length ? (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSearch({ category: undefined })}
            className={cn(
              'rounded-full border px-3.5 py-1.5 text-sm font-semibold transition',
              !search.category ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line text-muted hover:text-ink',
            )}
          >
            Semua kategori
          </button>
          {categoriesData.categories.map((category) => (
            <button
              key={category.slug}
              type="button"
              onClick={() => setSearch({ category: category.slug })}
              className={cn(
                'rounded-full border px-3.5 py-1.5 text-sm font-semibold capitalize transition',
                search.category === category.slug
                  ? 'border-brand bg-brand-soft text-brand-ink'
                  : 'border-line text-muted hover:text-ink',
              )}
            >
              {category.slug}
              <span className="ml-1.5 text-xs text-subtle">{category.count}</span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-6 flex items-center justify-between gap-4 border-b border-line pb-4">
        <p className="text-sm text-muted" aria-live="polite">
          {isLoading ? 'Memuat kelas…' : `${formatNumber(meta?.total ?? 0)} kelas ditemukan`}
          {isFetching && !isLoading ? ' · menyegarkan' : ''}
        </p>

        {activeFilters > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ search: { sort: search.sort, page: 1 } })}
          >
            <X className="size-4" />
            Hapus {activeFilters} filter
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <CourseCardSkeleton key={index} />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <EmptyState illustration="box"
          className="card mt-8"
          icon={<SlidersHorizontal className="size-6" />}
          title="Tidak ada kelas yang cocok"
          description="Coba ganti kata kunci atau longgarkan filter yang sedang aktif."
          action={
            <Button variant="outline" onClick={() => navigate({ search: {} })}>
              Atur ulang pencarian
            </Button>
          }
        />
      ) : (
        <>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((course) => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>

          {meta && meta.total_pages > 1 ? (
            <nav className="mt-10 flex items-center justify-center gap-2" aria-label="Navigasi halaman">
              <Button
                variant="outline"
                size="sm"
                disabled={meta.page <= 1}
                onClick={() => navigate({ search: (previous) => ({ ...previous, page: meta.page - 1 }) })}
              >
                Sebelumnya
              </Button>
              <span className="px-3 text-sm text-muted">
                Halaman {meta.page} dari {meta.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={meta.page >= meta.total_pages}
                onClick={() => navigate({ search: (previous) => ({ ...previous, page: meta.page + 1 }) })}
              >
                Berikutnya
              </Button>
            </nav>
          ) : null}
        </>
      )}
    </div>
  )
}
